import { useEffect, useRef, useState } from "react";
import * as THREE from "three";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const COUNT = 8000;

const BOOT_POLY = [
  [0.30, 1.00], [0.64, 1.00], [0.66, 0.92], [0.62, 0.90], [0.62, 0.46],
  [0.68, 0.34], [0.90, 0.28], [1.00, 0.20], [1.00, 0.08], [0.94, 0.02],
  [0.10, 0.02], [0.06, 0.10], [0.08, 0.16], [0.24, 0.18], [0.28, 0.44],
  [0.28, 0.90], [0.26, 0.92],
];

function starPoly() {
  const pts = [];
  for (let i = 0; i < 10; i++) {
    const r = i % 2 === 0 ? 1 : 0.42;
    const a = -Math.PI / 2 + (i * Math.PI) / 5;
    pts.push([0.5 + r * Math.cos(a) * 0.5, 0.5 + r * Math.sin(a) * 0.5]);
  }
  return pts;
}

function inPoly(x, y, poly) {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const [xi, yi] = poly[i], [xj, yj] = poly[j];
    if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

function samplePoly(poly, count, scale) {
  const out = new Float32Array(count * 3);
  let i = 0;
  while (i < count) {
    const x = Math.random(), y = Math.random();
    if (inPoly(x, y, poly)) {
      out[i * 3] = (x - 0.5) * scale;
      out[i * 3 + 1] = (y - 0.5) * scale;
      out[i * 3 + 2] = (Math.random() - 0.5) * 0.7;
      i++;
    }
  }
  return out;
}

const VERT = `
attribute vec3 aBoot;
attribute vec3 aScatter;
attribute vec3 aExplode;
attribute vec3 aStar;
attribute float aRand;
uniform float uAssemble;
uniform float uScroll;
uniform float uTime;
uniform float uPR;
varying float vAlpha;
varying float vRand;
void main() {
  float a = clamp(uAssemble * 1.35 - aRand * 0.35, 0.0, 1.0);
  a = a * a * (3.0 - 2.0 * a);
  vec3 pos = mix(aScatter, aBoot, a);
  float p1 = smoothstep(0.0, 0.35, uScroll);
  float e = clamp(p1 * (0.65 + 0.7 * aRand), 0.0, 1.0);
  pos = mix(pos, aExplode, e);
  float p2 = smoothstep(0.35, 0.70, uScroll);
  pos = mix(pos, aStar, p2);
  pos.x += sin(uTime * (0.5 + aRand) + aRand * 43.0) * 0.03;
  pos.y += cos(uTime * (0.6 + aRand * 0.8) + aRand * 71.0) * 0.03;
  vec4 mv = modelViewMatrix * vec4(pos, 1.0);
  float depth = clamp(pos.z + 0.5, 0.0, 1.0);
  gl_PointSize = mix(1.2, 2.8, depth) * uPR;
  vAlpha = mix(0.30, 0.85, depth);
  vRand = aRand;
  gl_Position = projectionMatrix * mv;
}
`;

const FRAG = `
uniform vec3 uColor;
varying float vAlpha;
varying float vRand;
void main() {
  vec2 uv = gl_PointCoord - 0.5;
  float d = length(uv);
  if (d > 0.5) discard;
  float soft = smoothstep(0.5, 0.08, d);
  vec3 col = uColor * (0.8 + 0.4 * vRand);
  gl_FragColor = vec4(col, soft * vAlpha);
}
`;

export default function ParticleCanvas() {
  const wrapRef = useRef(null);
  const [assembled, setAssembled] = useState(false);

  useEffect(() => {
    const wrap = wrapRef.current;
    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: false });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setClearColor(0x000000, 0);
    wrap.appendChild(renderer.domElement);

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(50, 1, 0.1, 20);
    camera.position.z = 3.4;

    const boot = samplePoly(BOOT_POLY, COUNT, 2.6);
    for (let i = 0; i < COUNT; i++) boot[i * 3] += 1.5;
    const star = samplePoly(starPoly(), COUNT, 2.8);
    const scatter = new Float32Array(COUNT * 3);
    const explode = new Float32Array(COUNT * 3);
    const rand = new Float32Array(COUNT);
    for (let i = 0; i < COUNT; i++) {
      scatter[i * 3] = (Math.random() - 0.5) * 8;
      scatter[i * 3 + 1] = (Math.random() - 0.5) * 8;
      scatter[i * 3 + 2] = (Math.random() - 0.5) * 4;
      explode[i * 3] = (Math.random() - 0.5) * 10;
      explode[i * 3 + 1] = (Math.random() - 0.5) * 10;
      explode[i * 3 + 2] = (Math.random() - 0.5) * 6;
      rand[i] = Math.random();
    }

    const geo = new THREE.BufferGeometry();
    geo.setAttribute("position", new THREE.BufferAttribute(boot, 3));
    geo.setAttribute("aBoot", new THREE.BufferAttribute(boot, 3));
    geo.setAttribute("aStar", new THREE.BufferAttribute(star, 3));
    geo.setAttribute("aScatter", new THREE.BufferAttribute(scatter, 3));
    geo.setAttribute("aExplode", new THREE.BufferAttribute(explode, 3));
    geo.setAttribute("aRand", new THREE.BufferAttribute(rand, 1));

    const uniforms = {
      uAssemble: { value: 0 },
      uScroll: { value: 0 },
      uTime: { value: 0 },
      uPR: { value: Math.min(window.devicePixelRatio, 2) },
      uColor: { value: new THREE.Color("#D9622B") },
    };
    const mat = new THREE.ShaderMaterial({
      vertexShader: VERT,
      fragmentShader: FRAG,
      uniforms,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });
    const points = new THREE.Points(geo, mat);
    scene.add(points);

    const resize = () => {
      const { clientWidth: w, clientHeight: h } = wrap;
      renderer.setSize(w, h);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
    };
    resize();
    window.addEventListener("resize", resize);

    gsap.to(uniforms.uAssemble, { value: 1, duration: 1.4, ease: "power2.inOut", delay: 0.2, onComplete: () => setAssembled(true) });

    const st = ScrollTrigger.create({
      trigger: document.body,
      start: "top top",
      end: "max",
      scrub: 0.6,
      onUpdate: (self) => { uniforms.uScroll.value = self.progress; },
    });

    const clock = new THREE.Clock();
    let raf;
    const loop = () => {
      uniforms.uTime.value = clock.getElapsedTime();
      points.rotation.z = uniforms.uScroll.value * 0.5;
      renderer.render(scene, camera);
      raf = requestAnimationFrame(loop);
    };
    loop();

    return () => {
      cancelAnimationFrame(raf);
      st.kill();
      window.removeEventListener("resize", resize);
      geo.dispose(); mat.dispose(); renderer.dispose();
      wrap.removeChild(renderer.domElement);
    };
  }, []);

  return (
    <div ref={wrapRef} className="absolute inset-0 particle-wrap" data-testid="particle-canvas">
      {!assembled && <div className="absolute inset-0 shimmer" />}
    </div>
  );
}
